/**
 * The page `cova_changes.changes_since` returns, as the leaf reads it and the
 * wire carries it (spec §5.2 and §5.3).
 *
 * A PAGE IS A SET OF TABLE NAMES, never rows (spec ruling 17): `schema.table`
 * for every table that changed in the firm since the cursor, with no row ids
 * and no action, because the ledger's action is a free verb nothing on the
 * client may read.
 *
 * TWO CURSORS, ONE PER STREAM. The ledger stream and the change stream each
 * allocate seq under their own per-firm lock, so each is ordered and holes-free
 * on its own and the two have no merged order. The client holds both and sends
 * both back. A fresh tab sends none and gets both tails and no tables.
 *
 * `resync` means a cursor fell behind a stream's retained floor or sat above
 * its tail (plan ruling 18(d)): the page then carries both tails and no tables,
 * and the client invalidates everything once.
 *
 * Seqs are bigints on both sides of the wire boundary and strings on the wire,
 * since the ledger's seq outgrows a JavaScript number.
 */
import { Schema as S } from "effect"

/**
 * One stream's position. Never negative: the ledger's imported rows carry
 * negative seqs, and a live cursor of 0 or more never reaches them (spec §5.2
 * rule 2).
 */
export const ChangesSeq = S.BigInt.pipe(S.nonNegativeBigInt())
export type ChangesSeq = typeof ChangesSeq.Type

export const ChangesCursor = S.Struct({
  ledger: ChangesSeq,
  changes: ChangesSeq,
})
export type ChangesCursor = typeof ChangesCursor.Type

export const ChangesPage = S.Struct({
  /** Distinct `schema.table` names, sorted by the door. Empty on a fresh or resync page. */
  tables: S.Array(S.String),
  /** Where the next poll resumes: the highest raw seq each stream scanned, or each tail. */
  cursor: ChangesCursor,
  /** Either stream filled the page limit; the client polls again at once. */
  hasMore: S.Boolean,
  /** A cursor fell outside a stream's retained range; the client invalidates everything. */
  resync: S.Boolean,
  /**
   * The tables in `tables` whose every change in this page was written by a
   * transaction the caller named (`ChangesXids`): the caller's own saves,
   * which it has already refetched (query speed Q11). Absent or empty when it
   * named none. A table a co-worker also changed in the window is never here.
   */
  echoed: S.optional(S.Array(S.String)),
})
export type ChangesPage = typeof ChangesPage.Type

/** At most this many transaction ids per poll; the tab's store keeps no more (`own-writes.ts`). */
export const CHANGES_XIDS_MAX = 64

/** The largest `xid8`, 2^64 - 1. */
const XID8_MAX = BigInt("18446744073709551615")

/**
 * Whether a string is `xid8` text: a decimal the type can hold, the form
 * `pg_current_xact_id()::text` takes. A 20-digit number above 2^64 - 1 is
 * refused here, not by the `::xid8[]` cast, which would fail the whole poll
 * (round 1 nit 11).
 */
export const isXid8Text = (value: string): boolean => /^\d{1,20}$/.test(value) && BigInt(value) <= XID8_MAX

/**
 * The response header a BMS write names its transaction ids in, set by the
 * session lane (`write-echo-middleware.ts`) and read by the app's fetch
 * delegate (`app-http-client.ts`): one name, here in the feed's contract, for
 * both sides (round 1 nit 14).
 */
export const WRITE_ECHO_HEADER = "x-cova-xid"

/** A transaction id as `xid8` text (`isXid8Text`). */
export const ChangesXid = S.String.pipe(S.filter(isXid8Text, { message: () => "not an xid8: a decimal up to 2^64 - 1" }))

/** The caller's own recent transaction ids, named so the feed can mark its own writes `echoed`. */
export const ChangesXids = S.Array(ChangesXid).pipe(S.maxItems(CHANGES_XIDS_MAX))
export type ChangesXids = typeof ChangesXids.Type
